import useResidentNeeds from 'hooks/useResidentNeeds'
import type { ProfileNeedOption } from 'screens/Residents/screens/ResidentNeedsScreen/ResidentNeedsScreen'
import type {
  NeedId,
  SkillNeedOption
} from '../components/needs/LegacySkillNeeds'
import { unsupersededSkillNeedIds } from '../components/needs/LegacySkillNeeds'

/** A resident need as either source supplies it — the plan's own copy, or the v6 query. */
type LinkedNeed = { id: string; need?: { id?: NeedId } | null }

type UseLegacySkillNeedsArgs = {
  residentId: string
  /** The care plan's legacy `skillNeeds`. */
  skillNeeds?: NeedId[]
  /** ResidentNeed ids the plan links, as the form currently holds them. */
  linkedResidentNeedIds: string[]
  /** The plan's own populated resident needs, as saved. */
  populatedResidentNeeds: LinkedNeed[]
  /** Skill-need templates, which carry the display names. */
  needTemplates?: ProfileNeedOption[]
  /** False wherever the legacy tags are not rendered. Skips the query and the clear. */
  enabled: boolean
}

/**
 * The legacy `skillNeeds` side of a care plan review.
 *
 * Needs linked before MODs needs shipped live only on the legacy `skillNeeds` array, so the
 * review has to render them or they vanish once `mods_needs_care_plan` is on. Once every one
 * of them has an equivalent resident need linked, the array is dead weight that still renders
 * a duplicate tag on mobile, which reads both fields — so the save empties it.
 *
 * `enabled` is false wherever those tags are not on screen, which does two things: it stops
 * the clear writing away something the user was never shown, and it stops the query paging the
 * resident's whole need list for a review that cannot use it (a read-only user, or a home with
 * `mods_needs_care_plan` off, where the hook's legacy branch costs two requests rather than
 * one). It is the `id: ''` idiom `NeedsSection` uses.
 */
const useLegacySkillNeeds = ({
  residentId,
  skillNeeds,
  linkedResidentNeedIds,
  populatedResidentNeeds,
  needTemplates,
  enabled
}: UseLegacySkillNeedsArgs) => {
  const ids = enabled ? (skillNeeds?.map(String) ?? []) : []
  const isRelevant = ids.length > 0

  const { needs: queriedResidentNeeds } = useResidentNeeds({
    id: isRelevant ? residentId : ''
  })

  // Both sources, because neither is sufficient alone: the query returns [] until it resolves,
  // so a cold-cache save would never clear, and the plan's own array misses a need linked in
  // this session. The plan's copy is added last so it wins on id, which is also the source
  // `NeedsSection` resolves against — the two must not disagree about what "covered" means.
  const byId = new Map<string, LinkedNeed>(
    [...queriedResidentNeeds, ...populatedResidentNeeds].map((need) => [
      need.id,
      need
    ])
  )
  const linkedNeeds = linkedResidentNeedIds
    .map((linkedId) => byId.get(linkedId))
    .filter((need): need is LinkedNeed => Boolean(need))

  return {
    /** Legacy ids to render as tags. Empty wherever they are not shown. */
    ids,
    /** Templates flattened to what the tags need to name themselves. */
    options: needTemplates
      ?.flatMap((template) => template.needs ?? [])
      .map(
        (need): SkillNeedOption => ({
          id: String(need.id),
          name: need.name
        })
      ),
    /** Every legacy id is now covered, so the save can empty the array. */
    canClear:
      isRelevant && unsupersededSkillNeedIds(ids, linkedNeeds).length === 0
  }
}

export default useLegacySkillNeeds
